import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { AlertCircle, Clock, CreditCard, Info } from 'lucide-react';

interface CancelledSubscriptionsProps {
    subscriptions: any[];
}

const CancelledSubscriptions: React.FC<CancelledSubscriptionsProps> = ({ subscriptions }) => {
    const { t, i18n } = useTranslation();

    const formatDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString();

    const getStatusStyle = (status: string): string => {
        switch (status) {
            case 'canceled': return 'bg-destructive/10 text-destructive border-destructive/20';
            case 'incomplete_expired': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'unpaid': return 'bg-amber-100 text-amber-700 border-amber-200';
            default: return 'bg-secondary text-muted-foreground border-border';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'canceled': return <AlertCircle size={13} />;
            case 'incomplete_expired': return <Clock size={13} />;
            case 'unpaid': return <CreditCard size={13} />;
            default: return <AlertCircle size={13} />;
        }
    };

    const getPlanName = (subscription: any) => {
        const item = subscription.items?.data[0];
        if (item?.price?.product && typeof item.price.product === 'object') {
            return item.price.product.name || t('common.subscription.subscriptionPlan', 'Subscription Plan');
        }
        return item?.price?.nickname || item?.plan?.nickname || subscription.plan?.nickname
            || t('common.subscription.subscriptionPlan', 'Subscription Plan');
    };

    if (subscriptions.length === 0) return null;

    return (
        <div
            dir={getLanguageDirection(i18n.language)}
            className="mt-4 space-y-3"
        >
            <h3 className="text-sm font-semibold text-foreground">
                {t('common.subscription.previousSubscriptions')}
            </h3>

            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-gold/5 border border-gold/20 text-sm text-foreground">
                <Info size={15} className="text-gold shrink-0 mt-0.5" />
                <p>{t('common.subscription.existingCancelled')}</p>
            </div>

            {subscriptions.map((subscription) => (
                <div
                    key={subscription.id}
                    className="rounded-xl border border-border bg-card p-3 space-y-2"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground">
                            {getPlanName(subscription)}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${getStatusStyle(subscription.status)}`}>
                            {getStatusIcon(subscription.status)}
                            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1).replace('_', ' ')}
                        </span>
                    </div>

                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        {subscription.created && (
                            <span>{t('common.subscription.created')}: {formatDate(subscription.created)}</span>
                        )}
                        {subscription.canceled_at && (
                            <span>{t('common.subscription.cancelled')}: {formatDate(subscription.canceled_at)}</span>
                        )}
                        {subscription.ended_at && (
                            <span>{t('common.subscription.ended')}: {formatDate(subscription.ended_at)}</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default CancelledSubscriptions;
